<template>
	<router-link :to="url" class="flex min-w-0 flex-col text-ink" @click="$emit('open', product)">
		<div class="well relative aspect-square" :class="compact ? 'p-2.5' : 'p-3'">
			<img :src="imageSrc" :alt="product.name" loading="lazy" decoding="async" @error="imageFailed = true" />
			<span v-if="sale" class="absolute left-2 top-2 rounded-full bg-sale px-2 py-0.5 text-[11px] font-extrabold text-bg">Sale</span>
			<button
				v-if="!compact"
				type="button"
				class="absolute right-0.5 top-0.5 flex h-11 w-11 items-center justify-center text-[#453c50]"
				:aria-label="saved ? 'Saved to a wish list' : 'Save to wish list'"
				@click.prevent.stop="$emit('save', product)"
			>
				<span class="flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
					<Icon :name="saved ? 'heartFilled' : 'heart'" :size="18" :class="{ 'text-sale': saved }" />
				</span>
			</button>
		</div>
		<span v-if="kicker" class="mt-2 truncate text-xs font-bold uppercase tracking-[0.04em] text-muted">{{ kicker }}</span>
		<span class="clamp-2 font-semibold leading-snug" :class="[compact ? 'mt-2 text-sm' : 'text-[15px]', { 'mt-2': !kicker && !compact }]">{{ product.name }}</span>
		<span v-if="product.rating && !compact" class="mt-1 flex items-center gap-0.5 text-star" :aria-label="`Rated ${product.rating} out of 5`">
			<Icon v-for="n in 5" :key="n" name="star" :size="12" :class="{ 'opacity-30': n > Math.round(product.rating) }" />
		</span>
		<span class="mt-1 flex flex-wrap items-baseline gap-x-1.5">
			<template v-if="priceHidden">
				<span class="text-sm font-bold text-muted">{{ product.priceHiddenLabel || 'Call for pricing' }}</span>
			</template>
			<template v-else>
				<span v-if="range" class="text-xs font-semibold text-muted">From</span>
				<span class="price text-base" :class="{ 'text-sale': sale }">{{ priceText }}</span>
				<span v-if="sale" class="text-[13px] font-semibold text-muted line-through">{{ money(sale) }}</span>
			</template>
		</span>
		<span v-if="outOfStock" class="mt-0.5 text-[13px] font-semibold text-muted">Out of stock</span>
	</router-link>
</template>

<script>
import Icon from './Icon.vue'
import { money } from '@/lib/format'
import { productUrl, productThumb, displayPrice, wasPrice, isPriceHidden, showsAsOutOfStock, hasVariantPriceRange } from '@/lib/products'
import { isSaved } from '@/lib/wishlists'

export default {
	components: { Icon },
	inject: ['store'],
	props: {
		product: { type: Object, required: true },
		compact: { type: Boolean, default: false },
		kicker: { type: String, default: '' },
		queryID: { type: String, default: null },
		position: { type: Number, default: null },
	},
	emits: ['save', 'open'],
	data() {
		return { imageFailed: false }
	},
	computed: {
		url() {
			return productUrl(this.product, { queryID: this.queryID, position: this.position })
		},
		imageSrc() {
			return this.imageFailed ? '/images/product-placeholder.svg' : productThumb(this.product)
		},
		priceHidden() {
			return isPriceHidden(this.product)
		},
		priceText() {
			const price = displayPrice(this.product)
			return price == null ? '' : money(price)
		},
		range() {
			return hasVariantPriceRange(this.product)
		},
		sale() {
			return wasPrice(this.product)
		},
		outOfStock() {
			return showsAsOutOfStock(this.product)
		},
		saved() {
			return isSaved(this.store, this.product)
		},
	},
	methods: { money },
}
</script>
